<template>
  <div class="step-container client">
    <help-drawer :title="helpTitle">
      <div>
        <p>
          Authorized signers are individuals who have the legal authority to sign
          documents and make decisions on behalf of the entity.
        </p>
      </div>
    </help-drawer>
    <v-form name="AuthorizedSigners" class="form" data-test-id="authorized-signers-form">
      <v-container class="client-step-container">
        <syncing-indicator :pageName="MENUS.CLIENT.AUTHORIZED_SIGNERS.id" :instance="instance"></syncing-indicator>
        <v-row class="mb-8">
          <v-col class="instructions">
            <h1 data-test-id="authorized-signers-header">
              <span class="headerlight">
                <strong>Authorized signer information</strong> for
                <strong>{{ label }}</strong>
              </span>
            </h1>
            <div class="help-link">
              <a @click="toggleHelp()" data-test-id="help-link">{{ helpTitle }}</a>
            </div>
          </v-col>
        </v-row>
        
        <!-- Show syncing message when syncing -->
        <div v-if="isSyncing" class="syncing-message" data-test-id="syncing-message">
          <p>Saving authorized signer information...</p>
        </div>
        
        <!-- Show data submitted message only when completed, not syncing, and form hidden -->
        <data-submitted-message
          v-if="isAuthorizedSignersComplete && formHidden && !isSyncing"
          :submitted="isAuthorizedSignersComplete"
          v-on:hide-form="toggleFormState"
          data-test-id="data-submitted-message"
        />
        
        <!-- Hide form content when syncing or when formHidden is true -->
        <v-row class="justify-center" v-if="!formHidden && !isSyncing">
          <v-col class="step-form-container large">
            
            <!-- Are you the authorized signer? -->
            <v-row justify="center">
              <v-col cols="12" md="6" class="text-center">
                <label class="required mb-2 d-block" data-test-id="interviewer-signer-label">
                  Are you the authorized signer?
                </label>
                <div class="d-flex justify-center">
                  <v-radio-group
                    v-model="interviewerIsSigner"
                    :error-messages="interviewerIsSignerErrors"
                    hide-details="auto"
                    row
                    @change="handleInterviewerChange"
                    data-test-id="interviewer-signer-radio-group"
                    class="authorized-signer-toggle"
                  >
                    <v-radio
                      :ripple="false"
                      class="v-faux-radio half-width first"
                      label="Yes"
                      :value="true"
                      data-test-id="interviewer-signer-yes"
                    ></v-radio>
                    <v-radio
                      :ripple="false"
                      class="v-faux-radio half-width"
                      label="No"
                      :value="false"
                      data-test-id="interviewer-signer-no"
                    ></v-radio>
                  </v-radio-group>
                </div>
              </v-col>
            </v-row>
            
            <!-- Add Another Signer shortcut when interviewer is the signer -->
            <v-row v-if="interviewerIsSigner === true && addAnotherSignerSelected === false" class="mt-4 mb-4">
              <v-col cols="12" class="text-center">
                <v-btn
                  outlined
                  color="primary"
                  @click="startAddingAnotherSigner"
                  data-test-id="add-signer-button"
                >
                  <v-icon left>mdi-plus</v-icon>
                  Add Another Signer
                </v-btn>
              </v-col>
            </v-row>
            
            <!-- Signer Details (if interviewer is not signer OR adding another signer while interviewer is signer) -->
            <v-expand-transition>
              <div v-if="interviewerIsSigner === false || addAnotherSignerSelected === true" class="mt-4">
                <div v-for="(signer, index) in signers" :key="index" class="mb-4">
                  <v-card class="pa-4" elevation="0" outlined>
                    <h3 class="mb-4">Authorized Signer {{ (interviewerIsSigner === true && addAnotherSignerSelected === true) ? (index + 2) : (index + 1) }}</h3>
                
                    <!-- Basic Information -->
                    <h4 class="mb-3">Basic Information</h4>
                    <v-row>
                      <v-col cols="12" sm="6">
                        <label :for="`firstName-${index}`" class="required" data-test-id="first-name-label">
                          First Name
                        </label>
                        <v-text-field
                          outlined
                          type="text"
                          maxlength="256"
                          :name="`firstName-${index}`"
                          aria-required="true"
                          v-model.trim="signer.firstName"
                          :backgroundColor="inputBackgroundColor"
                          @input="$v.signers.$each[index].firstName.$touch()"
                          @blur="$v.signers.$each[index].firstName.$touch()"
                          :error-messages="getFirstNameErrors(index)"
                          hide-details="auto"
                          dense
                          :data-test-id="`first-name-input-${index}`"
                        />
                      </v-col>
                      <v-col cols="12" sm="6">
                        <label :for="`lastName-${index}`" class="required" data-test-id="last-name-label">
                          Last Name
                        </label>
                        <v-text-field
                          outlined
                          type="text"
                          maxlength="256"
                          :name="`lastName-${index}`"
                          aria-required="true"
                          v-model.trim="signer.lastName"
                          :backgroundColor="inputBackgroundColor"
                          @input="$v.signers.$each[index].lastName.$touch()"
                          @blur="$v.signers.$each[index].lastName.$touch()"
                          :error-messages="getLastNameErrors(index)"
                          hide-details="auto"
                          dense
                          :data-test-id="`last-name-input-${index}`"
                        />
                      </v-col>
                    </v-row>

                    <!-- Contact Information -->
                    <v-row class="mt-2">
                      <v-col cols="12" sm="3">
                        <label :for="`email-${index}`" class="required" data-test-id="email-label">
                          Email
                        </label>
                        <v-text-field
                          outlined
                          type="email"
                          maxlength="256"
                          :name="`email-${index}`"
                          aria-required="true"
                          v-model.trim="signer.email"
                          :backgroundColor="inputBackgroundColor"
                          @input="$v.signers.$each[index].email.$touch()"
                          @blur="$v.signers.$each[index].email.$touch()"
                          :error-messages="getEmailErrors(index)"
                          hide-details="auto"
                          dense
                          :data-test-id="`email-input-${index}`"
                        />
                      </v-col>
                      <v-col cols="12" sm="3">
                        <label :for="`phone-${index}`" data-test-id="phone-label">
                          Phone
                        </label>
                        <v-text-field
                          outlined
                          type="tel"
                          maxlength="14"
                          :name="`phone-${index}`"
                          v-model="signer.phone"
                          placeholder="###-###-####"
                          :backgroundColor="inputBackgroundColor"
                          @input="formatPhone(index)"
                          hide-details="auto"
                          dense
                          :data-test-id="`phone-input-${index}`"
                        />
                      </v-col>
                      <v-col cols="12" sm="3">
                        <label :for="`dob-${index}`" class="required" data-test-id="dob-label">
                          Date of Birth
                        </label>
                        <v-text-field
                          v-model="signer.dob"
                          :placeholder="INPUT_DATE_FMT"
                          outlined
                          :name="`dob-${index}`"
                          maxlength="8"
                          :backgroundColor="inputBackgroundColor"
                          @input="$v.signers.$each[index].dob.$touch()"
                          @blur="standardizeDob(index)"
                          :error-messages="getDobErrors(index)"
                          hide-details="auto"
                          inputmode="numeric"
                          dense
                          :data-test-id="`dob-input-${index}`"
                        >
                          <template v-slot:append>
                            <v-icon small color="#9b9b9b">
                              fas fa-calendar-alt
                            </v-icon>
                          </template>
                        </v-text-field>
                      </v-col>
                      <v-col cols="12" sm="3">
                        <label :for="`ssn-${index}`" class="required" data-test-id="ssn-label">
                          SSN / Tax ID
                        </label>
                        <v-text-field
                          outlined
                          type="text"
                          maxlength="11"
                          :name="`ssn-${index}`"
                          aria-required="true"
                          v-model="signer.ssn"
                          placeholder="###-##-####"
                          :backgroundColor="inputBackgroundColor"
                          @input="formatSsn(index)"
                          @blur="$v.signers.$each[index].ssn.$touch()"
                          :error-messages="getSsnErrors(index)"
                          hide-details="auto"
                          dense
                          :data-test-id="`ssn-input-${index}`"
                        />
                      </v-col>
                    </v-row>

                    <!-- Authority Level -->
                    <v-row class="mt-2">
                      <v-col cols="12" sm="3">
                        <label :for="`authorityLevel-${index}`" class="required" data-test-id="authority-level-label">
                          Authority Level
                        </label>
                        <v-select
                          v-model="signer.authorityLevel"
                          :items="authorityLevels"
                          :backgroundColor="inputBackgroundColor"
                          outlined
                          dense
                          hide-details="auto"
                          @change="$v.signers.$each[index].authorityLevel.$touch()"
                          :error-messages="getAuthorityLevelErrors(index)"
                          :data-test-id="`authority-level-select-${index}`"
                        />
                      </v-col>
                    </v-row>
                    
                    <!-- Personal Information -->
                    <h4 class="mt-4 mb-3">Personal Information</h4>
                    <v-row>
                      <v-col cols="12" sm="3">
                        <label :for="`secondaryPhone-${index}`" data-test-id="secondary-phone-label">
                          Secondary Phone
                        </label>
                        <v-text-field
                          outlined
                          type="tel"
                          maxlength="14"
                          :name="`secondaryPhone-${index}`"
                          v-model="signer.secondaryPhone"
                          placeholder="###-###-####"
                          :backgroundColor="inputBackgroundColor"
                          @input="formatSecondaryPhone(index)"
                          hide-details="auto"
                          dense
                          :data-test-id="`secondary-phone-input-${index}`"
                        />
                      </v-col>
                      <v-col cols="12" sm="3">
                        <label class="required mb-2" data-test-id="us-resident-label">
                          US Permanent Resident?
                        </label>
                        <v-radio-group
                          v-model="signer.usPermanentResident"
                          :error-messages="getUsPermanentResidentErrors(index)"
                          hide-details="auto"
                          row
                          @change="$v.signers.$each[index].usPermanentResident.$touch()"
                          :data-test-id="`us-resident-radio-group-${index}`"
                          class="mt-1"
                        >
                          <v-radio
                            :ripple="false"
                            label="Yes"
                            :value="true"
                            :data-test-id="`us-resident-yes-${index}`"
                          ></v-radio>
                          <v-radio
                            :ripple="false"
                            label="No"
                            :value="false"
                            :data-test-id="`us-resident-no-${index}`"
                          ></v-radio>
                        </v-radio-group>
                      </v-col>
                      <v-col cols="12" sm="3">
                        <label class="required mb-2" data-test-id="ten-percent-label">
                          10% Shareholder?
                        </label>
                        <v-radio-group
                          v-model="signer.tenPercentShareholder"
                          :error-messages="getTenPercentShareholderErrors(index)"
                          hide-details="auto"
                          row
                          @change="$v.signers.$each[index].tenPercentShareholder.$touch()"
                          :data-test-id="`ten-percent-radio-group-${index}`"
                          class="mt-1"
                        >
                          <v-radio
                            :ripple="false"
                            label="Yes"
                            :value="true"
                            :data-test-id="`ten-percent-yes-${index}`"
                          ></v-radio>
                          <v-radio
                            :ripple="false"
                            label="No"
                            :value="false"
                            :data-test-id="`ten-percent-no-${index}`"
                          ></v-radio>
                        </v-radio-group>
                      </v-col>
                      <v-col cols="12" sm="3">
                        <label class="required mb-2" data-test-id="finra-member-label">
                          FINRA Member?
                        </label>
                        <v-radio-group
                          v-model="signer.finraMember"
                          :error-messages="getFinraMemberErrors(index)"
                          hide-details="auto"
                          row
                          @change="$v.signers.$each[index].finraMember.$touch()"
                          :data-test-id="`finra-member-radio-group-${index}`"
                          class="mt-1"
                        >
                          <v-radio
                            :ripple="false"
                            label="Yes"
                            :value="true"
                            :data-test-id="`finra-member-yes-${index}`"
                          ></v-radio>
                          <v-radio
                            :ripple="false"
                            label="No"
                            :value="false"
                            :data-test-id="`finra-member-no-${index}`"
                          ></v-radio>
                        </v-radio-group>
                      </v-col>
                    </v-row>
                    
                    <!-- Additional Personal Information Row -->
                    <v-row class="mt-2">
                      <v-col cols="12" sm="3">
                        <label class="required mb-2" data-test-id="politically-exposed-label">
                          Politically Exposed?
                        </label>
                        <v-radio-group
                          v-model="signer.politicallyExposed"
                          :error-messages="getPoliticallyExposedErrors(index)"
                          hide-details="auto"
                          row
                          @change="$v.signers.$each[index].politicallyExposed.$touch()"
                          :data-test-id="`politically-exposed-radio-group-${index}`"
                          class="mt-1"
                        >
                          <v-radio
                            :ripple="false"
                            label="Yes"
                            :value="true"
                            :data-test-id="`politically-exposed-yes-${index}`"
                          ></v-radio>
                          <v-radio
                            :ripple="false"
                            label="No"
                            :value="false"
                            :data-test-id="`politically-exposed-no-${index}`"
                          ></v-radio>
                        </v-radio-group>
                      </v-col>
                    </v-row>
                    
                    <!-- Financial & Background Information -->
                    <v-row class="mt-2">
                      <v-col cols="12" sm="3">
                        <label :for="`citizenship-${index}`" class="required" data-test-id="citizenship-label">
                          Citizenship
                        </label>
                        <v-select
                          v-model="signer.citizenship"
                          :items="countries"
                          :backgroundColor="inputBackgroundColor"
                          outlined
                          dense
                          hide-details="auto"
                          @change="$v.signers.$each[index].citizenship.$touch()"
                          :error-messages="getCitizenshipErrors(index)"
                          :data-test-id="`citizenship-select-${index}`"
                        />
                      </v-col>
                      <v-col cols="12" sm="3">
                        <label :for="`sourceOfWealth-${index}`" class="required" data-test-id="source-wealth-label">
                          Source of Wealth
                        </label>
                        <v-select
                          v-model="signer.sourceOfWealth"
                          :items="wealthSources"
                          :backgroundColor="inputBackgroundColor"
                          outlined
                          dense
                          hide-details="auto"
                          @change="$v.signers.$each[index].sourceOfWealth.$touch()"
                          :error-messages="getSourceOfWealthErrors(index)"
                          :data-test-id="`source-wealth-select-${index}`"
                        />
                      </v-col>
                      <v-col cols="12" sm="3">
                        <label :for="`annualIncome-${index}`" class="required" data-test-id="annual-income-label">
                          Annual Income
                        </label>
                        <v-text-field
                          outlined
                          type="number"
                          :name="`annualIncome-${index}`"
                          aria-required="true"
                          v-model.number="signer.annualIncome"
                          prefix="$"
                          :backgroundColor="inputBackgroundColor"
                          @input="$v.signers.$each[index].annualIncome.$touch()"
                          @blur="$v.signers.$each[index].annualIncome.$touch()"
                          :error-messages="getAnnualIncomeErrors(index)"
                          hide-details="auto"
                          dense
                          :data-test-id="`annual-income-input-${index}`"
                        />
                      </v-col>
                    </v-row>

                    <!-- Employment Information -->
                    <h4 class="mt-4 mb-3">Employment Information</h4>
                    <v-row>
                      <v-col cols="12" sm="6">
                        <label :for="`employmentStatus-${index}`" class="required" data-test-id="employment-status-label">
                          Employment Status
                        </label>
                        <v-select
                          v-model="signer.employmentStatus"
                          :items="employmentStatuses"
                          :backgroundColor="inputBackgroundColor"
                          outlined
                          dense
                          hide-details="auto"
                          @change="handleEmploymentStatusChange(index)"
                          :error-messages="getEmploymentStatusErrors(index)"
                          :data-test-id="`employment-status-select-${index}`"
                        />
                      </v-col>
                      <v-col cols="12" sm="6" v-if="isEmployed(signer.employmentStatus)">
                        <label :for="`employerDetails-${index}`" class="required" data-test-id="employer-details-label">
                          Employer Name
                        </label>
                        <v-text-field
                          outlined
                          type="text"
                          maxlength="256"
                          :name="`employerDetails-${index}`"
                          aria-required="true"
                          v-model.trim="signer.employerDetails"
                          :backgroundColor="inputBackgroundColor"
                          @input="$v.signers.$each[index].employerDetails.$touch()"
                          @blur="$v.signers.$each[index].employerDetails.$touch()"
                          :error-messages="getEmployerDetailsErrors(index)"
                          hide-details="auto"
                          dense
                          :data-test-id="`employer-details-input-${index}`"
                        />
                      </v-col>
                    </v-row>
                    <v-row v-if="isEmployed(signer.employmentStatus)">
                      <v-col cols="12" sm="6">
                        <label :for="`employerIndustry-${index}`" class="required" data-test-id="employer-industry-label">
                          Line of Business
                        </label>
                        <v-select
                          v-model="signer.employerIndustry"
                          :items="businessLines"
                          :backgroundColor="inputBackgroundColor"
                          outlined
                          dense
                          hide-details="auto"
                          @change="$v.signers.$each[index].employerIndustry.$touch()"
                          :error-messages="getEmployerIndustryErrors(index)"
                          :data-test-id="`employer-industry-select-${index}`"
                        />
                      </v-col>
                    </v-row>
                
                    <!-- Use Same Address Toggle -->
                    <v-row class="mt-4">
                      <v-col cols="12">
                        <label class="required mb-2" data-test-id="use-same-address-label">
                          Use same address as entity?
                        </label>
                        <v-radio-group
                          v-model="signer.useSameAddress"
                          :error-messages="getUseSameAddressErrors(index)"
                          hide-details="auto"
                          row
                          @change="$v.signers.$each[index].useSameAddress.$touch()"
                          :data-test-id="`use-same-address-radio-group-${index}`"
                        >
                          <v-radio
                            :ripple="false"
                            class="v-faux-radio half-width first"
                            label="Yes"
                            :value="true"
                            :data-test-id="`use-same-address-yes-${index}`"
                          ></v-radio>
                          <v-radio
                            :ripple="false"
                            class="v-faux-radio half-width"
                            label="No"
                            :value="false"
                            :data-test-id="`use-same-address-no-${index}`"
                          ></v-radio>
                        </v-radio-group>
                      </v-col>
                    </v-row>
                
                    <!-- Address (conditional) -->
                    <v-expand-transition>
                      <div v-if="signer.useSameAddress === false" class="mt-4">
                        <h4 class="mb-3">Signer Address</h4>
                        <label :for="`street-${index}`" class="required" data-test-id="street-label">
                          Street Address
                        </label>
                        <v-text-field
                          outlined
                          type="text"
                          maxlength="256"
                          :name="`street-${index}`"
                          aria-required="true"
                          v-model.trim="signer.address.street"
                          :backgroundColor="inputBackgroundColor"
                          @input="$v.signers.$each[index].address.street.$touch()"
                          @blur="$v.signers.$each[index].address.street.$touch()"
                          :error-messages="getAddressStreetErrors(index)"
                          hide-details="auto"
                          class="mb-4"
                          :data-test-id="`street-input-${index}`"
                        />
                        
                        <v-row class="mt-2">
                          <v-col cols="12" sm="6">
                            <label :for="`city-${index}`" class="required" data-test-id="city-label">
                              City
                            </label>
                            <v-text-field
                              outlined
                              type="text"
                              maxlength="256"
                              :name="`city-${index}`"
                              aria-required="true"
                              v-model.trim="signer.address.city"
                              :backgroundColor="inputBackgroundColor"
                              @input="$v.signers.$each[index].address.city.$touch()"
                              @blur="$v.signers.$each[index].address.city.$touch()"
                              :error-messages="getAddressCityErrors(index)"
                              hide-details="auto"
                              :data-test-id="`city-input-${index}`"
                            />
                          </v-col>
                          <v-col cols="12" sm="3">
                            <label :for="`state-${index}`" class="required" data-test-id="state-label">
                              State
                            </label>
                            <v-select
                              v-model="signer.address.state"
                              :items="states"
                              :backgroundColor="inputBackgroundColor"
                              outlined
                              dense
                              hide-details="auto"
                              @change="$v.signers.$each[index].address.state.$touch()"
                              :error-messages="getAddressStateErrors(index)"
                              :data-test-id="`state-select-${index}`"
                            />
                          </v-col>
                          <v-col cols="12" sm="3">
                            <label :for="`zipCode-${index}`" class="required" data-test-id="zip-label">
                              ZIP Code
                            </label>
                            <v-text-field
                              outlined
                              type="text"
                              maxlength="10"
                              :name="`zipCode-${index}`"
                              aria-required="true"
                              v-model="signer.address.zipCode"
                              :backgroundColor="inputBackgroundColor"
                              @input="formatZipCode(index)"
                              @blur="$v.signers.$each[index].address.zipCode.$touch()"
                              :error-messages="getAddressZipErrors(index)"
                              hide-details="auto"
                              :data-test-id="`zip-input-${index}`"
                            />
                          </v-col>
                        </v-row>
                        
                        <v-row class="mt-2">
                          <v-col cols="12" sm="6">
                            <label :for="`country-${index}`" class="required" data-test-id="country-label">
                              Country
                            </label>
                            <v-select
                              v-model="signer.address.country"
                              :items="countries"
                              :backgroundColor="inputBackgroundColor"
                              outlined
                              dense
                              hide-details="auto"
                              @change="$v.signers.$each[index].address.country.$touch()"
                              :error-messages="getAddressCountryErrors(index)"
                              :data-test-id="`country-select-${index}`"
                            />
                          </v-col>
                        </v-row>
                      </div>
                    </v-expand-transition>
                
                    <!-- Remove Signer Button -->
                    <v-row v-if="(interviewerIsSigner === false && index === 1) || (interviewerIsSigner === true && addAnotherSignerSelected === true && index === 0)" class="mt-3">
                      <v-col cols="12" class="text-right">
                        <v-btn
                          outlined
                          color="error"
                          @click="removeSigner(index)"
                          data-test-id="remove-signer-button"
                        >
                          <v-icon left small>mdi-delete</v-icon>
                          Remove Signer
                        </v-btn>
                      </v-col>
                    </v-row>
                  </v-card>
                </div>
              
                <!-- Add Another Signer Button (only in "No" flow) -->
                <v-row v-if="interviewerIsSigner === false && signers.length < 2" class="mt-4 mb-4">
                  <v-col cols="12">
                    <v-btn
                      outlined
                      color="primary"
                      @click="addSigner"
                      data-test-id="add-signer-button"
                    >
                      <v-icon left>mdi-plus</v-icon>
                      Add Another Signer
                    </v-btn>
                  </v-col>
                </v-row>
              </div>
            </v-expand-transition>
            
            <div class="skip-for-now">
              <a @click="skip()" data-test-id="skip-button">Skip for now</a>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </v-form>
    <bottom-nav v-on:go-prev="prev()" v-on:go-next="next()" data-test-id="authorized-signers-nav"></bottom-nav>
  </div>
</template>

<script>
import HelpDrawer from '@/components/HelpDrawerComponent';
import BottomNav from '@/components/common/BottomNav';
import DataSubmittedMessage from '@/components/client/DataSubmittedMessage';
import InterviewClientMixin from '@/common/mixins/interview.client.mixin';
import { MENUS, STATES, COUNTRIES, WEALTH_SOURCE_LIST, BUSINESS_LINE_LIST, CLIENT_EMPLOYMENT_STATUSES, INPUT_DATE_FMT, validInputDate, validateZip } from '@/common/constants';
import { required, requiredIf, email, numeric } from 'vuelidate/lib/validators';
import SyncingIndicator from '@/components/common/SyncingIndicator.vue';

const createEmptySigner = () => ({
  firstName: null,
  lastName: null,
  email: null,
  phone: null,
  secondaryPhone: null,
  useSameAddress: null,
  address: {
    street: null,
    city: null,
    state: null,
    zipCode: null,
    country: 'US',
  },
  authorityLevel: null,
  citizenship: null,
  usPermanentResident: null,
  tenPercentShareholder: null,
  finraMember: null,
  politicallyExposed: null,
  sourceOfWealth: null,
  employmentStatus: null,
  employerDetails: null,
  employerIndustry: null,
  annualIncome: null,
  dob: null,
  ssn: null,
});

export default {
  name: 'AuthorizedSigners',

  components: { BottomNav, HelpDrawer, DataSubmittedMessage, SyncingIndicator },
  mixins: [InterviewClientMixin],
  
  props: {
    name: {
      type: String,
      required: true,
    },
    label: {
      type: String,
      required: true,
    },
  },

  data: () => ({
    interviewerIsSigner: null,
    signers: [createEmptySigner()],
    addAnotherSignerSelected: false,
    authorityLevels: [
      { text: 'Full Authority', value: 'Full' },
      { text: 'Limited Authority', value: 'Limited' },
    ],
    employmentStatuses: Object.values(CLIENT_EMPLOYMENT_STATUSES),
    states: STATES,
    countries: COUNTRIES,
    wealthSources: Object.values(WEALTH_SOURCE_LIST),
    businessLines: Object.values(BUSINESS_LINE_LIST),
    MENUS: MENUS,
    INPUT_DATE_FMT: INPUT_DATE_FMT,
  }),
  
  validations() {
    const validations = {
      interviewerIsSigner: { required },
      signers: {
        $each: {
          firstName: { 
            required: requiredIf(function() {
              return this.interviewerIsSigner === false || this.addAnotherSignerSelected === true;
            })
          },
          lastName: { 
            required: requiredIf(function() {
              return this.interviewerIsSigner === false || this.addAnotherSignerSelected === true;
            })
          },
          email: { 
            required: requiredIf(function() {
              return this.interviewerIsSigner === false || this.addAnotherSignerSelected === true;
            }),
            email: email,
          },
          phone: {},
          secondaryPhone: {},
          dob: { 
            required: requiredIf(function() {
              return this.interviewerIsSigner === false || this.addAnotherSignerSelected === true;
            }),
            validInputDate,
          },
          ssn: { 
            required: requiredIf(function() {
              return this.interviewerIsSigner === false || this.addAnotherSignerSelected === true;
            }),
            validSsn(value) {
              if (!value) return true;
              const cleaned = value.replace(/[^0-9]/g, '');
              return cleaned.length === 9;
            }
          },
          usPermanentResident: { 
            required: requiredIf(function() {
              return this.interviewerIsSigner === false || this.addAnotherSignerSelected === true;
            })
          },
          tenPercentShareholder: { 
            required: requiredIf(function() {
              return this.interviewerIsSigner === false || this.addAnotherSignerSelected === true;
            })
          },
          finraMember: { 
            required: requiredIf(function() {
              return this.interviewerIsSigner === false || this.addAnotherSignerSelected === true;
            })
          },
          politicallyExposed: { 
            required: requiredIf(function() {
              return this.interviewerIsSigner === false || this.addAnotherSignerSelected === true;
            })
          },
          citizenship: { 
            required: requiredIf(function() {
              return this.interviewerIsSigner === false || this.addAnotherSignerSelected === true;
            })
          },
          sourceOfWealth: { 
            required: requiredIf(function() {
              return this.interviewerIsSigner === false || this.addAnotherSignerSelected === true;
            })
          },
          employmentStatus: { 
            required: requiredIf(function() {
              return this.interviewerIsSigner === false || this.addAnotherSignerSelected === true;
            })
          },
          employerDetails: { 
            required: requiredIf(function() {
              const signer = this.signers[this.$v.signers.$each.$iter];
              return (this.interviewerIsSigner === false || this.addAnotherSignerSelected === true) && signer && this.isEmployed(signer.employmentStatus);
            })
          },
          employerIndustry: { 
            required: requiredIf(function() {
              const signer = this.signers[this.$v.signers.$each.$iter];
              return (this.interviewerIsSigner === false || this.addAnotherSignerSelected === true) && signer && this.isEmployed(signer.employmentStatus);
            })
          },
          annualIncome: { 
            required: requiredIf(function() {
              return this.interviewerIsSigner === false || this.addAnotherSignerSelected === true;
            }),
            numeric,
          },
          useSameAddress: { 
            required: requiredIf(function() {
              return this.interviewerIsSigner === false || this.addAnotherSignerSelected === true;
            })
          },
          address: {
            street: { 
              required: requiredIf(function() {
                const signer = this.signers[this.$v.signers.$each.$iter];
                return (this.interviewerIsSigner === false || this.addAnotherSignerSelected === true) && signer && !signer.useSameAddress;
              })
            },
            city: { 
              required: requiredIf(function() {
                const signer = this.signers[this.$v.signers.$each.$iter];
                return (this.interviewerIsSigner === false || this.addAnotherSignerSelected === true) && signer && !signer.useSameAddress;
              })
            },
            state: { 
              required: requiredIf(function() {
                const signer = this.signers[this.$v.signers.$each.$iter];
                return (this.interviewerIsSigner === false || this.addAnotherSignerSelected === true) && signer && !signer.useSameAddress;
              })
            },
            zipCode: { 
              required: requiredIf(function() {
                const signer = this.signers[this.$v.signers.$each.$iter];
                return (this.interviewerIsSigner === false || this.addAnotherSignerSelected === true) && signer && !signer.useSameAddress;
              }),
              validateZip
            },
            country: { 
              required: requiredIf(function() {
                const signer = this.signers[this.$v.signers.$each.$iter];
                return (this.interviewerIsSigner === false || this.addAnotherSignerSelected === true) && signer && !signer.useSameAddress;
              })
            },
          },
          authorityLevel: { 
            required: requiredIf(function() {
              return this.interviewerIsSigner === false || this.addAnotherSignerSelected === true;
            })
          },
        }
      },
      validationGroup: ['interviewerIsSigner', 'signers'],
    };
    
    return validations;
  },
  
  computed: {
    instance() {
      return {
        name: this.name,
        label: this.label,
      };
    },
    
    isAuthorizedSignersComplete() {
      const page = this.getPage(MENUS.CLIENT.AUTHORIZED_SIGNERS.id, this.instance);
      return page && page.lastCompleted;
    },
    
    isSyncing() {
      const page = this.getPage(MENUS.CLIENT.AUTHORIZED_SIGNERS.id, this.instance);
      return page && page.status === 'IN_PROGRESS';
    },
    
    helpTitle() {
      return 'What does this mean?';
    },
    
    interviewerIsSignerErrors() {
      const errors = [];
      if (!this.$v.interviewerIsSigner.$dirty) return errors;
      !this.$v.interviewerIsSigner.required && errors.push('This field is required');
      return errors;
    },
  },
  
  methods: {
    isEmployed(status) {
      return status === CLIENT_EMPLOYMENT_STATUSES.FULL_TIME_EMPLOYED ||
             status === CLIENT_EMPLOYMENT_STATUSES.PART_TIME_EMPLOYED ||
             status === CLIENT_EMPLOYMENT_STATUSES.SELF_EMPLOYED;
    },
    
    // Error getters for dynamic validation
    getFirstNameErrors(index) {
      const errors = [];
      if (!this.$v.signers.$each[index]) return errors;
      if (!this.$v.signers.$each[index].firstName.$dirty) return errors;
      !this.$v.signers.$each[index].firstName.required && errors.push('First name is required');
      return errors;
    },
    
    getLastNameErrors(index) {
      const errors = [];
      if (!this.$v.signers.$each[index]) return errors;
      if (!this.$v.signers.$each[index].lastName.$dirty) return errors;
      !this.$v.signers.$each[index].lastName.required && errors.push('Last name is required');
      return errors;
    },
    
    getEmailErrors(index) {
      const errors = [];
      if (!this.$v.signers.$each[index]) return errors;
      if (!this.$v.signers.$each[index].email.$dirty) return errors;
      !this.$v.signers.$each[index].email.required && errors.push('Email is required');
      !this.$v.signers.$each[index].email.email && errors.push('Please enter a valid email');
      return errors;
    },
    
    getAuthorityLevelErrors(index) {
      const errors = [];
      if (!this.$v.signers.$each[index]) return errors;
      if (!this.$v.signers.$each[index].authorityLevel.$dirty) return errors;
      !this.$v.signers.$each[index].authorityLevel.required && errors.push('Authority level is required');
      return errors;
    },

    getDobErrors(index) {
      const errors = [];
      if (!this.$v.signers.$each[index]) return errors;
      if (!this.$v.signers.$each[index].dob.$dirty) return errors;
      !this.$v.signers.$each[index].dob.required && errors.push('Date of birth is required');
      !this.$v.signers.$each[index].dob.validInputDate && errors.push('Please enter a valid date');
      return errors;
    },
    
    getSsnErrors(index) {
      const errors = [];
      if (!this.$v.signers.$each[index]) return errors;
      if (!this.$v.signers.$each[index].ssn.$dirty) return errors;
      !this.$v.signers.$each[index].ssn.required && errors.push('SSN is required');
      !this.$v.signers.$each[index].ssn.validSsn && errors.push('Please enter a valid SSN');
      return errors;
    },
    
    getUsPermanentResidentErrors(index) {
      const errors = [];
      if (!this.$v.signers.$each[index]) return errors;
      if (!this.$v.signers.$each[index].usPermanentResident.$dirty) return errors;
      !this.$v.signers.$each[index].usPermanentResident.required && errors.push('This field is required');
      return errors;
    },
    
    getTenPercentShareholderErrors(index) {
      const errors = [];
      if (!this.$v.signers.$each[index]) return errors;
      if (!this.$v.signers.$each[index].tenPercentShareholder.$dirty) return errors;
      !this.$v.signers.$each[index].tenPercentShareholder.required && errors.push('This field is required');
      return errors;
    },
    
    getFinraMemberErrors(index) {
      const errors = [];
      if (!this.$v.signers.$each[index]) return errors;
      if (!this.$v.signers.$each[index].finraMember.$dirty) return errors;
      !this.$v.signers.$each[index].finraMember.required && errors.push('This field is required');
      return errors;
    },
    
    getPoliticallyExposedErrors(index) {
      const errors = [];
      if (!this.$v.signers.$each[index]) return errors;
      if (!this.$v.signers.$each[index].politicallyExposed.$dirty) return errors;
      !this.$v.signers.$each[index].politicallyExposed.required && errors.push('This field is required');
      return errors;
    },
    
    getCitizenshipErrors(index) {
      const errors = [];
      if (!this.$v.signers.$each[index]) return errors;
      if (!this.$v.signers.$each[index].citizenship.$dirty) return errors;
      !this.$v.signers.$each[index].citizenship.required && errors.push('Citizenship is required');
      return errors;
    },
    
    getSourceOfWealthErrors(index) {
      const errors = [];
      if (!this.$v.signers.$each[index]) return errors;
      if (!this.$v.signers.$each[index].sourceOfWealth.$dirty) return errors;
      !this.$v.signers.$each[index].sourceOfWealth.required && errors.push('Source of wealth is required');
      return errors;
    },
    
    getEmploymentStatusErrors(index) {
      const errors = [];
      if (!this.$v.signers.$each[index]) return errors;
      if (!this.$v.signers.$each[index].employmentStatus.$dirty) return errors;
      !this.$v.signers.$each[index].employmentStatus.required && errors.push('Employment status is required');
      return errors;
    },
    
    getEmployerDetailsErrors(index) {
      const errors = [];
      if (!this.$v.signers.$each[index]) return errors;
      if (!this.$v.signers.$each[index].employerDetails.$dirty) return errors;
      !this.$v.signers.$each[index].employerDetails.required && errors.push('Employer name is required');
      return errors;
    },
    
    getEmployerIndustryErrors(index) {
      const errors = [];
      if (!this.$v.signers.$each[index]) return errors;
      if (!this.$v.signers.$each[index].employerIndustry.$dirty) return errors;
      !this.$v.signers.$each[index].employerIndustry.required && errors.push('Line of business is required');
      return errors;
    },
    
    getAnnualIncomeErrors(index) {
      const errors = [];
      if (!this.$v.signers.$each[index]) return errors;
      if (!this.$v.signers.$each[index].annualIncome.$dirty) return errors;
      !this.$v.signers.$each[index].annualIncome.required && errors.push('Annual income is required');
      !this.$v.signers.$each[index].annualIncome.numeric && errors.push('Please enter a valid number');
      return errors;
    },
    
    getUseSameAddressErrors(index) {
      const errors = [];
      if (!this.$v.signers.$each[index]) return errors;
      if (!this.$v.signers.$each[index].useSameAddress.$dirty) return errors;
      !this.$v.signers.$each[index].useSameAddress.required && errors.push('This field is required');
      return errors;
    },
    
    getAddressStreetErrors(index) {
      const errors = [];
      if (!this.$v.signers.$each[index]) return errors;
      if (!this.$v.signers.$each[index].address.street.$dirty) return errors;
      !this.$v.signers.$each[index].address.street.required && errors.push('Street address is required');
      return errors;
    },
    
    getAddressCityErrors(index) {
      const errors = [];
      if (!this.$v.signers.$each[index]) return errors;
      if (!this.$v.signers.$each[index].address.city.$dirty) return errors;
      !this.$v.signers.$each[index].address.city.required && errors.push('City is required');
      return errors;
    },
    
    getAddressStateErrors(index) {
      const errors = [];
      if (!this.$v.signers.$each[index]) return errors;
      if (!this.$v.signers.$each[index].address.state.$dirty) return errors;
      !this.$v.signers.$each[index].address.state.required && errors.push('State is required');
      return errors;
    },
    
    getAddressZipErrors(index) {
      const errors = [];
      if (!this.$v.signers.$each[index]) return errors;
      if (!this.$v.signers.$each[index].address.zipCode.$dirty) return errors;
      !this.$v.signers.$each[index].address.zipCode.required && errors.push('ZIP code is required');
      !this.$v.signers.$each[index].address.zipCode.validateZip && errors.push('ZIP code must be NNNNN or NNNNN-NNNN');
      return errors;
    },
    
    getAddressCountryErrors(index) {
      const errors = [];
      if (!this.$v.signers.$each[index]) return errors;
      if (!this.$v.signers.$each[index].address.country.$dirty) return errors;
      !this.$v.signers.$each[index].address.country.required && errors.push('Country is required');
      return errors;
    },
    
    handleInterviewerChange() {
      this.$v.interviewerIsSigner.$touch();
      if (this.interviewerIsSigner === true) {
        // Keep radio on Yes; reset add-another-signer flow but preserve ability to add later
        this.addAnotherSignerSelected = false;
        this.signers = [createEmptySigner()];
        if (this.$v && this.$v.signers) this.$v.signers.$reset();
      }
    },
    
    addSigner() {
      if (this.signers.length < 2) {
        this.signers.push(createEmptySigner());
      }
    },
    
    removeSigner(index) {
      // In "No" flow, allow removing second signer
      if (this.interviewerIsSigner === false && index === 1 && this.signers.length === 2) {
        this.signers.splice(index, 1);
        return;
      }

      // In "Yes" flow with additional signer, allow removing the only extra signer
      if (this.interviewerIsSigner === true && this.addAnotherSignerSelected === true && index === 0 && this.signers.length >= 1) {
        this.signers = [createEmptySigner()];
        this.addAnotherSignerSelected = false;
        if (this.$v && this.$v.signers) this.$v.signers.$reset();
      }
    },
    
    startAddingAnotherSigner() {
      // Stay on Yes but show additional signer entry flow
      this.addAnotherSignerSelected = true;
      if (!this.signers || this.signers.length === 0) {
        this.signers = [createEmptySigner()];
      }
      this.$nextTick(() => {
        if (this.$v && this.$v.signers) {
          this.$v.signers.$reset();
        }
      });
    },
    
    formatPhone(index) {
      let value = this.signers[index].phone;
      if (!value) return;
      
      // Remove all non-numeric characters
      value = value.replace(/[^0-9]/g, '');
      
      // Format as ###-###-####
      if (value.length > 3 && value.length <= 6) {
        value = value.slice(0, 3) + '-' + value.slice(3);
      } else if (value.length > 6) {
        value = value.slice(0, 3) + '-' + value.slice(3, 6) + '-' + value.slice(6, 10);
      }
      
      this.signers[index].phone = value;
    },

    formatSecondaryPhone(index) {
      let value = this.signers[index].secondaryPhone;
      if (!value) return;
      
      // Remove all non-numeric characters
      value = value.replace(/[^0-9]/g, '');
      
      // Format as ###-###-####
      if (value.length > 3 && value.length <= 6) {
        value = value.slice(0, 3) + '-' + value.slice(3);
      } else if (value.length > 6) {
        value = value.slice(0, 3) + '-' + value.slice(3, 6) + '-' + value.slice(6, 10);
      }
      
      this.signers[index].secondaryPhone = value;
    },

    formatSsn(index) {
      let value = this.signers[index].ssn;
      if (!value) return;
      
      // Remove all non-numeric characters
      value = value.replace(/[^0-9]/g, '');
      
      // Format as ###-##-####
      if (value.length > 3 && value.length <= 5) {
        value = value.slice(0, 3) + '-' + value.slice(3);
      } else if (value.length > 5) {
        value = value.slice(0, 3) + '-' + value.slice(3, 5) + '-' + value.slice(5, 9);
      }
      
      this.signers[index].ssn = value;
      this.$v.signers.$each[index].ssn.$touch();
    },
    
    standardizeDob(index) {
      if (!this.signers[index].dob) return;
      
      let date = this.signers[index].dob.replace(/[^0-9]/g, '');
      if (date.length === 8) {
        // Format as MM/DD/YYYY
        date = date.slice(0, 2) + '/' + date.slice(2, 4) + '/' + date.slice(4, 8);
        this.signers[index].dob = date;
      }
    },
    
    handleEmploymentStatusChange(index) {
      this.$v.signers.$each[index].employmentStatus.$touch();
      
      // Clear employment fields if not employed
      if (!this.isEmployed(this.signers[index].employmentStatus)) {
        this.signers[index].employerDetails = null;
        this.signers[index].employerIndustry = null;
      }
    },
    
    formatZipCode(index) {
      let value = this.signers[index].address.zipCode;
      if (!value) return;
      
      // Remove all non-numeric characters
      value = value.replace(/[^0-9]/g, '');
      
      // Format as ##### or #####-####
      if (value.length > 5) {
        value = value.slice(0, 5) + '-' + value.slice(5, 9);
      }
      
      this.signers[index].address.zipCode = value;
      this.$v.signers.$each[index].address.zipCode.$touch();
    },
    
    next(route = null) {
      const to = route || this.pages[this.pageIndex + 1];

      if (!this.formHidden) {
        this.$v.$touch();
        if (this.$v.$invalid) return;
      } else {
        this.goNext(to);
        return;
      }

      const data = {
        instance: this.instance,
        interviewerIsSigner: this.interviewerIsSigner,
      };
      
      if (this.interviewerIsSigner === true) {
        // If interviewer is signer, always send interviewer as authorizedSigner1
        data.authorizedSigner1 = { isInterviewer: true };

        // If adding another signer, include them as authorizedSigner2
        if (this.addAnotherSignerSelected && this.signers[0]) {
          const signerData = {
            isInterviewer: false,
            firstName: this.signers[0].firstName,
            lastName: this.signers[0].lastName,
            email: this.signers[0].email,
            phone: this.signers[0].phone,
            secondaryPhone: this.signers[0].secondaryPhone,
            authorityLevel: this.signers[0].authorityLevel,
            citizenship: this.signers[0].citizenship,
            usPermanentResident: this.signers[0].usPermanentResident,
            tenPercentShareholder: this.signers[0].tenPercentShareholder,
            finraMember: this.signers[0].finraMember,
            politicallyExposed: this.signers[0].politicallyExposed,
            sourceOfWealth: this.signers[0].sourceOfWealth,
            employmentStatus: this.signers[0].employmentStatus,
            employerDetails: this.signers[0].employerDetails,
            employerIndustry: this.signers[0].employerIndustry,
            annualIncome: this.signers[0].annualIncome,
            ssn: this.signers[0].ssn,
          };

          if (this.signers[0].dob) {
            const dateParts = this.signers[0].dob.split('/');
            if (dateParts.length === 3) {
              signerData.dob = `${dateParts[2]}-${dateParts[0].padStart(2, '0')}-${dateParts[1].padStart(2, '0')}`;
            }
          }

          if (!this.signers[0].useSameAddress) {
            signerData.address = {
              line1: this.signers[0].address.street,
              city: this.signers[0].address.city,
              state: this.signers[0].address.state,
              zip: this.signers[0].address.zipCode,
              country: this.signers[0].address.country,
            };
          }

          data.authorizedSigner2 = signerData;
        }
      } else {
        // Map signers array to authorizedSigner1 and authorizedSigner2
        if (this.signers[0]) {
          const signer1Data = {
            isInterviewer: false,
            firstName: this.signers[0].firstName,
            lastName: this.signers[0].lastName,
            email: this.signers[0].email,
            phone: this.signers[0].phone,
            secondaryPhone: this.signers[0].secondaryPhone,
            authorityLevel: this.signers[0].authorityLevel,
            citizenship: this.signers[0].citizenship,
            usPermanentResident: this.signers[0].usPermanentResident,
            tenPercentShareholder: this.signers[0].tenPercentShareholder,
            finraMember: this.signers[0].finraMember,
            politicallyExposed: this.signers[0].politicallyExposed,
            sourceOfWealth: this.signers[0].sourceOfWealth,
            employmentStatus: this.signers[0].employmentStatus,
            employerDetails: this.signers[0].employerDetails,
            employerIndustry: this.signers[0].employerIndustry,
            annualIncome: this.signers[0].annualIncome,
            ssn: this.signers[0].ssn,
          };
          
          // Convert DOB to ISO format
          if (this.signers[0].dob) {
            const dateParts = this.signers[0].dob.split('/');
            if (dateParts.length === 3) {
              signer1Data.dob = `${dateParts[2]}-${dateParts[0].padStart(2, '0')}-${dateParts[1].padStart(2, '0')}`;
            }
          }
          
          // Only include address if useSameAddress is false
          if (!this.signers[0].useSameAddress) {
            signer1Data.address = {
              line1: this.signers[0].address.street,
              city: this.signers[0].address.city,
              state: this.signers[0].address.state,
              zip: this.signers[0].address.zipCode,
              country: this.signers[0].address.country,
            };
          }
          
          data.authorizedSigner1 = signer1Data;
        }
        
        if (this.signers[1]) {
          const signer2Data = {
            isInterviewer: false,
            firstName: this.signers[1].firstName,
            lastName: this.signers[1].lastName,
            email: this.signers[1].email,
            phone: this.signers[1].phone,
            secondaryPhone: this.signers[1].secondaryPhone,
            authorityLevel: this.signers[1].authorityLevel,
            citizenship: this.signers[1].citizenship,
            usPermanentResident: this.signers[1].usPermanentResident,
            tenPercentShareholder: this.signers[1].tenPercentShareholder,
            finraMember: this.signers[1].finraMember,
            politicallyExposed: this.signers[1].politicallyExposed,
            sourceOfWealth: this.signers[1].sourceOfWealth,
            employmentStatus: this.signers[1].employmentStatus,
            employerDetails: this.signers[1].employerDetails,
            employerIndustry: this.signers[1].employerIndustry,
            annualIncome: this.signers[1].annualIncome,
            ssn: this.signers[1].ssn,
          };
          
          // Convert DOB to ISO format
          if (this.signers[1].dob) {
            const dateParts = this.signers[1].dob.split('/');
            if (dateParts.length === 3) {
              signer2Data.dob = `${dateParts[2]}-${dateParts[0].padStart(2, '0')}-${dateParts[1].padStart(2, '0')}`;
            }
          }
          
          // Only include address if useSameAddress is false
          if (!this.signers[1].useSameAddress) {
            signer2Data.address = {
              line1: this.signers[1].address.street,
              city: this.signers[1].address.city,
              state: this.signers[1].address.state,
              zip: this.signers[1].address.zipCode,
              country: this.signers[1].address.country,
            };
          }
          
          data.authorizedSigner2 = signer2Data;
        }
      }

      this.markPageAsSyncing(MENUS.CLIENT.AUTHORIZED_SIGNERS.id, this.instance);

      this.completeStep(MENUS.CLIENT.AUTHORIZED_SIGNERS.id, data, this.instance)
        .then(() => {
          this.formHidden = true;
          this.goNext(to);
        })
        .catch(() => {});
    },

    prev() {
      this.goPrev(this.pages[this.pageIndex - 1]);
    },

    skip() {
      this.$store.dispatch('GET_INTERVIEW_PROGRESS').finally(() => {
        this.goNext(this.pages[this.pageIndex + 1]);
      });
    },
    
    toggleFormState(hide) {
      this.formHidden = hide;
      
      this.$nextTick(() => {
        if (this.$v) {
          this.$v.$reset();
        }
      });
    },
    
    handleSyncStatusChange() {
      if (this.isAuthorizedSignersComplete) {
        this.formHidden = true;
      }
    }
  },
  
  watch: {
    isSyncing(newVal, oldVal) {
      if (oldVal === true && newVal === false) {
        this.$nextTick(() => {
          this.handleSyncStatusChange();
        });
      }
    },
    
    isAuthorizedSignersComplete(newVal) {
      if (newVal === true) {
        this.formHidden = true;
      }
    }
  },
  
  created() {
    // Get the current page index based on name and instance
    this.pages = this.interviewPages.getInterviewPages();
    
    // Find the index of this specific authorized signers page
    this.pageIndex = this.pages.findIndex(page => {
      if (typeof page === 'object' && page.name === MENUS.CLIENT.AUTHORIZED_SIGNERS.id) {
        return page.params && page.params.name === this.name && page.params.label === this.label;
      }
      return false;
    });

    this.formHidden = this.isAuthorizedSignersComplete;
    
    // Load existing data if available
    const existingPage = this.getPage(MENUS.CLIENT.AUTHORIZED_SIGNERS.id, this.instance);
    if (existingPage && existingPage.data) {
      if (existingPage.data.interviewerIsSigner !== undefined) {
        this.interviewerIsSigner = existingPage.data.interviewerIsSigner;
      }
      if (existingPage.data.signers) {
        this.signers = existingPage.data.signers;
      }
    }
  },
  
  mounted() {
    this.$nextTick(() => {
      if (this.isAuthorizedSignersComplete) {
        this.formHidden = true;
      }
    });
    
    this.$store.dispatch('GET_INTERVIEW_PROGRESS');
  }
};
</script>

<style lang="scss" scoped>
.syncing-message {
  text-align: center;
  margin: 20px 0;
  
  p {
    color: #666;
    font-style: italic;
  }
}

.authorized-signer-toggle {
  margin: 0 auto;
  display: inline-flex !important;
}

.authorized-signer-toggle .v-input__control {
  width: auto !important;
}

.authorized-signer-toggle .v-input__slot {
  margin: 0 auto;
}

h4 {
  font-size: 1.1rem;
  font-weight: 500;
  color: #424242;
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 8px;
}
</style>
